import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import { allpayments } from 'models/allpayments'; // Importing allpayments model
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the historical cash flow view.
 * @extends JetView
 */
export default class tableHistoricalCashFlowView extends JetView {
    /**
     * Configures the UI components of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function

        // Extending the summTreeColumn filter for custom summation behavior
        webix.ui.datafilter.summTreeColumn = webix.extend(
            {
                refresh: function (master, node, value) {
                    var result = 0; // Variable to hold the summation result
                    var resultFormat = 0; // Variable for formatted result
                    master.data.each(function (obj) {
                        if (obj.$level == 1) result += obj[value.columnId]; // Summing values at level 1
                    });
                    // Formatting the result based on the column ID
                    if (value.columnId === 'CantidadTickets') {
                        resultFormat = webix.i18n.intFormat(result); // Integer format
                    } else {
                        resultFormat = webix.i18n.priceFormat(result); // Price format
                    }
                    node.firstChild.innerHTML = resultFormat; // Updating the node with formatted result
                },
            },
            webix.ui.datafilter.summColumn, // Extending from existing summColumn filter
        );

        // Returning the layout configuration
        return {
            rows: [
                {
                    id: 'preloader', // ID for the preloader element
                    cols: [
                        {
                            view: 'treetable', // Defining a tree table view
                            id: 'treetable', // ID for the tree table
                            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
                            scheme: {
                                $group: {
                                    by: 'Punto', // Grouping by 'Punto'
                                    css: 'Total-bold', // CSS class for group rows
                                    footer: {
                                        MMoneda: ['MMoneda', 'sum'], // Footer configuration for MMoneda
                                        Fecha: ["<span style='font-weight:bold;'>Total:</span>", 'string'], // Footer configuration for Fecha
                                        CantidadTickets: ['CantidadTickets', 'sum'], // Footer configuration for CantidadTickets
                                        ValorEntradasEfectivo: ['ValorEntradasEfectivo', 'sum'], // Footer configuration for ValorEntradasEfectivo
                                        ValorEntradasBonoTC: ['ValorEntradasBonoTC', 'sum'], // Footer configuration for ValorEntradasBonoTC
                                        ValorEntradasTraslados: ['ValorEntradasTraslados', 'sum'], // Footer configuration for ValorEntradasTraslados
                                        ValorEntradasRecargas: ['ValorEntradasRecargas', 'sum'], // Footer configuration for ValorEntradasRecargas
                                        ValorSalidasEfectivo: ['ValorSalidasEfectivo', 'sum'], // Footer configuration for ValorSalidasEfectivo
                                        ValorSalidasTraslados: ['ValorSalidasTraslados', 'sum'], // Footer configuration for ValorSalidasTraslados
                                        ValorSalidasNotasRetiro: ['ValorSalidasNotasRetiro', 'sum'], // Footer configuration for ValorSalidasNotasRetiro
                                        Saldo: ['Saldo', 'sum'], // Footer configuration for Saldo
                                    },
                                    map: {
                                        MMoneda: ['MMoneda', 'sum'], // Mapping for MMoneda
                                        CantidadTickets: ['CantidadTickets', 'sum'], // Mapping for CantidadTickets
                                        ValorEntradasEfectivo: ['ValorEntradasEfectivo', 'sum'], // Mapping for ValorEntradasEfectivo
                                        ValorEntradasBonoTC: ['ValorEntradasBonoTC', 'sum'], // Mapping for ValorEntradasBonoTC
                                        ValorEntradasTraslados: ['ValorEntradasTraslados', 'sum'], // Mapping for ValorEntradasTraslados
                                        ValorEntradasRecargas: ['ValorEntradasRecargas', 'sum'], // Mapping for ValorEntradasRecargas
                                        ValorSalidasEfectivo: ['ValorSalidasEfectivo', 'sum'], // Mapping for ValorSalidasEfectivo
                                        ValorSalidasTraslados: ['ValorSalidasTraslados', 'sum'], // Mapping for ValorSalidasTraslados
                                        ValorSalidasNotasRetiro: ['ValorSalidasNotasRetiro', 'sum'], // Mapping for ValorSalidasNotasRetiro
                                        Saldo: ['Saldo', 'sum'], // Mapping for Saldo
                                    },
                                },
                            },
                            bready: function () {
                                this.openAll(); // Expands all rows when ready
                            },
                            footer: true, // Enables footer
                            scrollY: true, // Enables vertical scrolling
                            columns: [
                                {
                                    id: 'Fecha', // Column ID for Fecha
                                    header: _('Fecha'), // Header text for Fecha
                                    width: 250, // Column width
                                    template: function (obj, common) {
                                        if (obj.$group)
                                            return (
                                                common.treetable(obj, common) + obj.value // Returns group value for grouped rows
                                            );
                                        return obj.Fecha; // Returns Fecha for non-grouped rows
                                    },
                                    footer: _('Total:'), // Footer text for Fecha
                                },
                                {
                                    id: 'Moneda', // Column ID for Moneda
                                    header: _('Moneda'), // Header text for Moneda
                                    width: 100, // Column width
                                },
                                {
                                    id: 'CantidadTickets', // Column ID for CantidadTickets
                                    header: _('Cantidad Tickets'), // Header text for CantidadTickets
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.intFormat, // Formatting function for integers
                                    width: 150, // Column width
                                },
                                {
                                    id: 'ValorEntradasEfectivo', // Column ID for ValorEntradasEfectivo
                                    header: _('Valor Entradas Efectivo'), // Header text for ValorEntradasEfectivo
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 180, // Column width
                                },
                                {
                                    id: 'ValorEntradasBonoTC', // Column ID for ValorEntradasBonoTC
                                    header: _('Valor Entradas Bono / T.C.'), // Header text for ValorEntradasBonoTC
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 190, // Column width
                                },
                                {
                                    id: 'ValorEntradasTraslados', // Column ID for ValorEntradasTraslados
                                    header: _('Valor Entradas Traslados'), // Header text for ValorEntradasTraslados
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 190, // Column width
                                },
                                {
                                    id: 'ValorEntradasRecargas', // Column ID for ValorEntradasRecargas
                                    header: _('Valor Entradas Recargas'), // Header text for ValorEntradasRecargas
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 190, // Column width
                                },
                                {
                                    id: 'ValorSalidasEfectivo', // Column ID for ValorSalidasEfectivo
                                    header: _('Valor Salidas Efectivo'), // Header text for ValorSalidasEfectivo
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 170, // Column width
                                },
                                {
                                    id: 'ValorSalidasTraslados', // Column ID for ValorSalidasTraslados
                                    header: _('Valor Salidas Traslados'), // Header text for ValorSalidasTraslados
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 180, // Column width
                                },
                                {
                                    id: 'ValorSalidasNotasRetiro', // Column ID for ValorSalidasNotasRetiro
                                    header: _('Valor Salidas Notas de Retiro'), // Header text for ValorSalidasNotasRetiro
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 160, // Column width
                                },
                                {
                                    id: 'Saldo', // Column ID for Saldo
                                    header: _('Saldo'), // Header text for Saldo
                                    footer: { content: 'summTreeColumn' }, // Footer content for summation
                                    format: webix.i18n.priceFormat, // Formatting function for prices
                                    exportType: 'number', // Export type for this column
                                    width: 150, // Column width
                                },
                            ],
                            ready: function () {
                                if (!this.count()) {
                                    webix.extend(this, webix.OverlayBox); // Extending with OverlayBox for no data overlay
                                    this.showOverlay(
                                        "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                                            _('No hay datos') + // Overlay message for no data
                                            '</div>',
                                    );
                                }
                            },
                            autowidth: false, // Disables automatic width adjustment
                            math: true, // Enables mathematical operations
                            datafetch: 10, // Number of rows to fetch at a time
                            pager: 'pagerA', // ID for the pager component
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The grid component to initialize.
     */
    init(grid) {
        grid = $$('treetable'); // Getting the tree table instance
        var url = devConfig.geturlApi() + 'es/Financial/GetHistoricalCashFlow'; // API endpoint for fetching data

        // Event listener for filtering historical cash flow data
        this.on(this.app, 'historicalCashFlow:filter', (filterData) => {
            var object = Object.entries(filterData); // Converting filter data to entries
            var string1 = ''; // Query string for API request
            for (var i = 0; i < object.length; i++) {
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    if (object[i][0] == 'Datepicker') {
                        var date = object[i][1].split(' - '); // Splitting date range
                        console.log(date); // Logging date range
                        string1 = string1 + '&dateFrom=' + date[0]; // Adding dateFrom to query string
                        string1 = string1 + '&dateTo=' + date[1]; // Adding dateTo to query string
                    } else {
                        string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Adding other filter parameters
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        var object2 = Object.entries(object[i][1]); // Converting nested object to entries
                        for (var j = 0; j < object2.length; j++) {
                            if (object2[j][1] !== null) {
                                if (object2[j][0] === 'start') {
                                    var d = new Date(object2[j][1]); // Creating date object for start date
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2); // Formatting start date
                                    string1 = string1 + '&' + dateFrom; // Adding formatted start date to query string
                                } else {
                                    if (object2[j][0] === 'end') {
                                        var d = new Date(object2[j][1]); // Creating date object for end date
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2); // Formatting end date
                                        string1 = string1 + '&' + dateTo; // Adding formatted end date to query string
                                    }
                                }
                            }
                        }
                    }
                }
            }
            grid.clearAll(); // Clearing existing grid data
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + string1); // Loading new data with filters
            $$('filterPopup').hide(); // Hiding the filter popup
        });

        // Event listener for item click in the grid
        grid.attachEvent('onItemClick', function (id, e, node) {
            var item = this.getItem(id); // Getting the clicked item
            this.$scope.app.callEvent('user:id', [item.Id]); // Triggering user ID event
        });

        // Event listener for player selection
        this.on(this.app, 'player:select', (Id) => {
            grid.load(
                url + '?start=0&count=' + grid.data.$pagesize + '&PlayerId=' + Id, // Loading data based on selected player ID
            );
        });
    }
}

// FILE DOCUMENTED
